import React from 'react'
import { AsaasPaymentForm, PixPayment, BoletoPayment } from '../../payment/components'
import { PaymentMethod, Address } from '../../../shared/types/battery'
import { formatPaymentMethod, formatPrice } from '../../../shared/utils/checkoutHelpers'

interface CheckoutStep3Props {
  showPaymentForm: boolean
  shippingAddress: Address
  selectedPaymentMethod: PaymentMethod | null
  calculateTotal: () => number
  isLoadingPayment: boolean
  clientSecret: string | null
  orderId: string | null
  isCreatingOrder: boolean
  hasAttemptedOrderCreation: React.MutableRefObject<boolean>
  isAuthenticated: boolean
  user: {
    email: string
    name: string
    cpf?: string
  } | null
  handlePaymentSuccess: (paymentId: string) => Promise<void>
  handlePaymentError: (error: string) => void
  handles: Record<string, string>
  deliveryMethod?: 'shipping' | 'pickup' | null
  storeAddress?: Address
  onStartPayment: () => void
}

export const CheckoutStep3: React.FC<CheckoutStep3Props> = ({
  showPaymentForm,
  shippingAddress,
  selectedPaymentMethod,
  calculateTotal,
  isLoadingPayment,
  clientSecret,
  orderId,
  isCreatingOrder,
  hasAttemptedOrderCreation,
  isAuthenticated,
  user,
  handlePaymentSuccess,
  handlePaymentError,
  handles,
  deliveryMethod,
  storeAddress,
  onStartPayment
}) => {
  return (
    <div className={`${handles.formSection}`}>
      <h2 className={`${handles.formTitle} text-xl font-semibold text-gray-900 mb-6`}>
        Revisar Pedido
      </h2>

      {!showPaymentForm ? (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              {deliveryMethod === 'pickup' ? 'Endereço de Retirada' : 'Endereço de Entrega'}
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              {deliveryMethod === 'pickup' && storeAddress ? (
                <>
                  <p className="font-medium text-blue-800 mb-1">BaterPower - Loja Campinas</p>
                  <p className="font-medium">{storeAddress.street}, {storeAddress.number}</p>
                  {storeAddress.complement && <p>{storeAddress.complement}</p>}
                  <p>{storeAddress.neighborhood}, {storeAddress.city} - {storeAddress.state}</p>
                  <p>CEP: {storeAddress.zipCode}</p>
                  <div className="mt-2 text-sm text-green-600 font-medium">
                    ✓ Disponível para retirada com 2 horas após a confirmação do pagamento
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium">{shippingAddress.street}, {shippingAddress.number}</p>
                  {shippingAddress.complement && <p>{shippingAddress.complement}</p>}
                  <p>{shippingAddress.neighborhood}, {shippingAddress.city} - {shippingAddress.state}</p>
                  <p>CEP: {shippingAddress.zipCode}</p>
                </>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">
              Método de Pagamento
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium">
                {formatPaymentMethod(selectedPaymentMethod)}
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={onStartPayment}
              className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Finalizar Pagamento
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Resumo do Pedido</h3>
            <div className="text-sm text-blue-800 space-y-1">
              {deliveryMethod === 'pickup' && storeAddress ? (
                <p><strong>Retirada em:</strong> {storeAddress.street}, {storeAddress.number} - {storeAddress.city}/{storeAddress.state}</p>
              ) : (
                <p><strong>Entrega em:</strong> {shippingAddress.street}, {shippingAddress.number}, {shippingAddress.neighborhood}</p>
              )}
              <p><strong>Método:</strong> {formatPaymentMethod(selectedPaymentMethod)}</p>
              <p><strong>Total:</strong> {formatPrice(calculateTotal())}</p>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Dados do Pagamento
            </h3>

            {(selectedPaymentMethod === PaymentMethod.CREDIT_CARD || selectedPaymentMethod === PaymentMethod.DEBIT_CARD) && (
              <div id="payment-form-container">
                {isLoadingPayment ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Inicializando pagamento seguro...</p>
                  </div>
                ) : clientSecret ? (
                  <AsaasPaymentForm
                    amount={calculateTotal()}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    paymentMethod={selectedPaymentMethod as PaymentMethod.CREDIT_CARD | PaymentMethod.DEBIT_CARD}
                    clientSecret={clientSecret}
                    orderId={orderId || undefined}
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Preparando formulário de pagamento...</p>
                  </div>
                )}
              </div>
            )}

            {selectedPaymentMethod === PaymentMethod.PIX && isAuthenticated && user && (
              <>
                {isCreatingOrder ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Criando pedido...</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {hasAttemptedOrderCreation.current ? 'Processando...' : 'Aguardando...'}
                    </p>
                  </div>
                ) : orderId ? (
                  <PixPayment
                    amount={calculateTotal()}
                    customerEmail={user.email}
                    customerName={user.name}
                    orderId={orderId}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Preparando pagamento PIX...</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {hasAttemptedOrderCreation.current ? 'Aguardando criação do pedido...' : 'Iniciando...'}
                    </p>
                  </div>
                )}
              </>
            )}

            {selectedPaymentMethod === PaymentMethod.BOLETO && isAuthenticated && user && (
              user.cpf ? (
                <BoletoPayment
                  amount={calculateTotal()}
                  customerEmail={user.email}
                  customerName={user.name}
                  customerDocument={user.cpf}
                  orderId={orderId}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800">
                    CPF é obrigatório para pagamento com boleto. Por favor, atualize seus dados no perfil.
                  </p>
                </div>
              )
            )}
          </div>
        </div>
      )}
    </div>
  )
}

